import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover as ReactPopover } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import { TooltipProps } from '@rc-component/tooltip/lib/Tooltip';
import { MenuLinkClassName } from '@lib/components/Menu/Menu';
import styles from './Popover.module.scss';

export type ChildProps = {
  handleToggle: (v?: boolean) => void;
  isOpen: boolean;
};

export type PopoverStateProps = { handleClose: () => void };

export type PopoverBody = ({
  handleClose,
}: PopoverStateProps) => React.ReactElement;

export type PopoverTrigger = TooltipProps['trigger'];

export type PopoverProps = {
  width?: number;
  children: (props: ChildProps) => React.ReactElement;
  body: PopoverBody;
  placement?: TooltipPlacement;
  trigger?: PopoverTrigger;
  closeCallback?: () => void;
};

export default function Popover(props: PopoverProps) {
  const {
    width = 200,
    placement = 'bottomLeft',
    body,
    children,
    trigger = 'hover',
    closeCallback = () => undefined,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleToggle = (newIsOpen?: boolean) => {
    if (newIsOpen !== undefined) {
      setIsOpen((prevState) => newIsOpen || !prevState);
    }
    if (newIsOpen === false) {
      closeCallback();
    }
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
    closeCallback();
  }, [closeCallback]);

  const content = () => (
    <div style={width ? { width: `${width}px` } : undefined} ref={popoverRef}>
      {body({ handleClose })}
    </div>
  );

  useEffect(() => {
    const handleClickInside = (event: Event) => {
      if (
        trigger === 'hover' &&
        popoverRef.current &&
        // @ts-ignore
        event?.target?.classList?.contains(MenuLinkClassName)
      ) {
        handleClose();
      }
    };

    // Attach the event listener to the popover element
    const element = popoverRef.current;
    if (element) {
      element.addEventListener('click', handleClickInside);
    }

    // Clean up the event listener
    return () => {
      if (element) {
        element.removeEventListener('click', handleClickInside);
      }
    };
  }, [handleClose, isOpen, trigger]);

  return (
    <ReactPopover
      arrow={false}
      content={content}
      onOpenChange={handleToggle}
      open={isOpen}
      placement={placement}
      trigger={trigger}
      classNames={{ root: styles.rootPopover }}
      destroyOnHidden
    >
      {children({ handleToggle, isOpen })}
    </ReactPopover>
  );
}
